<template>
  <SessionNav v-if="isSessionRoute" />
  <HeaderMenu v-else />
  <router-view/>
  <FooterMenu />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import HeaderMenu from './components/HeaderMenu.vue'
import FooterMenu from './components/FooterMenu.vue'
import SessionNav from './components/SessionNav.vue'

const route = useRoute()
const isSessionRoute = computed(() => {
  // Show session nav when route path is under /session OR when served on the session subdomain
  try {
    const host = (typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : ''
    if (host.startsWith('session.')) return true
  } catch (e) {
    // ignore
  }
  return route.path.startsWith('/session')
})
</script>

<style lang="scss">
:root {
  --bg: #f5f4f0;
  --surface: #ffffff;
  --ink: #1a1a1a;
  --muted: #666;
  --border: #ccc8be;
  --navy: #1b2d4f;
  --signal: #b83018;
  --stable: #2a6b4a;
  --transition-c: #7a4f00;
  --grid-line: rgba(27,45,79,0.07);
  --font-serif: 'Playfair Display', Georgia, serif;
  --font-mono: 'IBM Plex Mono', 'Courier New', monospace;
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

html { scroll-behavior: smooth; }

body {
  font-family: var(--font-mono);
  background: var(--bg);
  color: var(--ink);
  font-size: 14px;
  line-height: 1.6;
  /* graph paper grid */
  background-image:
    linear-gradient(var(--grid-line) 1px, transparent 1px),
    linear-gradient(90deg, var(--grid-line) 1px, transparent 1px);
  background-size: 40px 40px;
}

/* ─── UTILITY ───────────────────────────────────────── */
.container { max-width: 960px; margin: 0 auto; padding: 0 24px; }
.label {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--muted);
}
.divider {
  border: none;
  border-top: 1px solid var(--border);
  margin: 0;
}

/* ─── NAV ────────────────────────────────────────────── */
nav {
  position: fixed; top: 0; left: 0; right: 0;
  background: rgba(245,244,240,0.92);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--border);
  z-index: 100;
}
.nav-inner {
  max-width: 960px; margin: 0 auto; padding: 0 24px;
  display: flex; align-items: center; justify-content: space-between;
  height: 52px;
}
.nav-logo {
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 700;
  color: var(--navy);
  text-decoration: none;
  letter-spacing: -0.01em;
}
.nav-logo span {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.1em;
  color: var(--muted);
  margin-left: 8px;
  vertical-align: middle;
}
.nav-links { display: flex; gap: 32px; list-style: none; }
.nav-links a {
  color: var(--muted);
  text-decoration: none;
  font-size: 12px;
  letter-spacing: 0.05em;
  transition: color 0.2s;
  padding: 2px 0;
  border-bottom: 2px solid transparent;
}
.nav-links a:hover { color: var(--navy); }
.nav-links a.router-link-exact-active,
.nav-links a.active { color: var(--navy); border-bottom-color: var(--navy); }
.nav-cta {
  background: var(--navy);
  color: white !important;
  padding: 6px 16px;
  border-radius: 2px;
  letter-spacing: 0.05em;
}
.nav-cta:hover { background: #2a4070 !important; color: white !important; }

/* ─── HERO ───────────────────────────────────────────── */
#hero {
  padding-top: 120px;
  padding-bottom: 80px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  position: relative;
  overflow: hidden;
}
#hero::before {
  content: '';
  position: absolute;
  top: 0; right: 0;
  width: 400px; height: 100%;
  background: linear-gradient(135deg, transparent 60%, rgba(27,45,79,0.03) 100%);
  pointer-events: none;
}
.hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}
.hero-eyebrow {
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.hero-eyebrow::before {
  content: '';
  display: inline-block;
  width: 24px;
  height: 1px;
  background: var(--muted);
}
h1 {
  font-family: var(--font-serif);
  font-size: clamp(32px, 4vw, 46px);
  font-weight: 700;
  line-height: 1.15;
  color: var(--navy);
  letter-spacing: -0.02em;
  margin-bottom: 20px;
}
.hero-sub {
  font-size: 14px;
  color: var(--muted);
  line-height: 1.7;
  margin-bottom: 36px;
  max-width: 380px;
}

.finance-page {
  padding-top: 100px;
  padding-bottom: 72px;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 4px;
}

/* Shared hero (product pages) */
.hero { padding: 26px 22px; min-height: 160px; display: flex; align-items: center; }
.hero h1 {
  font-family: var(--font-serif);
  font-size: 34px;
  font-weight: 700;
  line-height: 1.15;
  color: var(--navy);
  letter-spacing: -0.02em;
  margin-bottom: 10px;
}
.hero p { color: var(--muted); font-size: 13px; max-width: 760px; }

.btn-row { margin-top: 16px; display: flex; gap: 10px; flex-wrap: wrap; }
.btn-primary {
  background: var(--navy);
  color: white;
  padding: 10px 18px;
  border: 1px solid var(--navy);
  border-radius: 2px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.08em;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
}
.btn-secondary {
  background: transparent;
  color: var(--navy);
  padding: 10px 18px;
  border: 1px solid var(--border);
  border-radius: 2px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.08em;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
}

/* Alerts: 3 columns */
.cols3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.step { padding: 22px; background: var(--surface); border: 1px solid var(--border); }
.step-num { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: var(--muted); margin-bottom: 8px; }
.step-title { font-family: var(--font-serif); font-size: 16px; color: var(--navy); margin-bottom: 8px; }
.step-text { font-size: 12px; color: var(--muted); line-height: 1.65; }

/* Example email card */
.email-card { padding: 0; overflow: hidden; }
.email-header {
  padding: 14px 16px;
  background: rgba(245,244,240,0.8);
  border-bottom: 1px solid var(--border);
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}
.email-body { padding: 18px 16px; }
.email-body h3 {
  font-family: var(--font-mono);
  font-size: 14px;
  letter-spacing: 0.04em;
  color: var(--signal);
  margin-bottom: 12px;
  font-weight: 500;
}
.email-grid { display: grid; grid-template-columns: 1fr; gap: 10px; }
.email-line { font-size: 12px; color: #333; }
.email-line strong { color: var(--navy); font-weight: 500; }
.email-note {
  margin-top: 10px;
  font-size: 11px;
  color: var(--muted);
  border-left: 2px solid var(--border);
  padding: 10px 12px;
  line-height: 1.6;
}

/* Pricing */
.pricing { padding: 22px; border: 1px solid var(--navy); }
.pricing h2 { font-family: var(--font-serif); font-size: 22px; color: var(--navy); margin-bottom: 10px; }
.price { font-family: var(--font-serif); font-size: 34px; color: var(--navy); margin-bottom: 4px; }
.price-sub { font-size: 12px; color: var(--muted); margin-bottom: 14px; }
.list { list-style: none; display: grid; gap: 6px; margin-bottom: 14px; }
.list li { font-size: 12px; color: #444; }

/* Signup */
.signup { padding: 22px; }
.signup-title { font-family: var(--font-serif); font-size: 20px; color: var(--navy); margin-bottom: 6px; }
.signup-sub { font-size: 12px; color: var(--muted); margin-bottom: 12px; }
.form { display: flex; gap: 8px; max-width: 520px; }
.input {
  flex: 1;
  background: rgba(245,244,240,0.55);
  border: 1px solid var(--border);
  color: var(--ink);
  padding: 10px 14px;
  font-family: var(--font-mono);
  font-size: 12px;
  border-radius: 2px;
  outline: none;
}
.btn {
  background: var(--navy);
  color: white;
  padding: 10px 16px;
  border: 1px solid var(--navy);
  border-radius: 2px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.06em;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
  display: inline-block;
}
.btn,
.btn-white {
  transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
}
.btn[disabled],
.btn-white[disabled] {
  opacity: 0.65;
  cursor: not-allowed;
}
.btn.flash-success {
  background: var(--stable);
  border-color: var(--stable);
}
.btn.flash-error {
  background: var(--signal);
  border-color: var(--signal);
}
.btn-white.flash-success {
  background: var(--stable);
  border-color: var(--stable);
  color: white;
}
.btn-white.flash-error {
  background: var(--signal);
  border-color: var(--signal);
  color: white;
}
.msg { margin-top: 10px; font-size: 12px; color: var(--muted); min-height: 18px; }
.msg.success { color: var(--stable); }
.msg.error { color: var(--signal); }
.trust { font-size: 11px; color: var(--muted); margin-top: 10px; }

/* Divergence page */
.svg-wrap { background: var(--bg); border: 1px solid var(--border); border-radius: 2px; padding: 14px; margin-top: 14px; }
.table-card { padding: 18px; }
.table-title { font-family: var(--font-mono); font-size: 12px; color: var(--muted); letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 10px; }
table { width: 100%; border-collapse: collapse; font-size: 12px; }
th, td { padding: 10px 8px; border-bottom: 1px solid var(--border); }
th { text-align: left; color: var(--muted); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; }
td.num { text-align: right; font-variant-numeric: tabular-nums; }
.sig { font-family: var(--font-mono); letter-spacing: 0.04em; }
.sig.high { color: var(--signal); }
.sig.watch { color: var(--transition-c); }
.sig.ok { color: var(--stable); }
.sig.neutral { color: var(--muted); }
.para { padding: 18px 18px 0; font-size: 12px; color: var(--muted); line-height: 1.75; }

@media (max-width: 720px) {
  .nav-links { display: none; }
  .cols3 { grid-template-columns: 1fr; }
  .form { flex-direction: column; }
}
.btn-group { display: flex; gap: 12px; flex-wrap: wrap; }
.btn-primary {
  background: var(--navy);
  color: white;
  padding: 10px 24px;
  border: 1px solid var(--navy);
  border-radius: 2px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.08em;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.2s;
  display: inline-block;
}
.btn-primary:hover { background: #2a4070; }
.btn-secondary {
  background: transparent;
  color: var(--navy);
  padding: 10px 24px;
  border: 1px solid var(--border);
  border-radius: 2px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.08em;
  cursor: pointer;
  text-decoration: none;
  transition: border-color 0.2s;
  display: inline-block;
}
.btn-secondary:hover { border-color: var(--navy); }

/* Hero chart */
.hero-chart-wrap {
  position: relative;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 20px;
  opacity: 0;
  animation: fadeUp 0.8s ease 0.3s forwards;
}
.hero-chart-title {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 12px;
}
.chart-annotation {
  position: absolute;
  right: 24px;
  top: 50px;
  font-size: 10px;
  color: var(--muted);
  text-align: right;
  line-height: 1.4;
}
.chart-annotation strong { color: var(--signal); display: block; }

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeIn {
  from { opacity: 0; } to { opacity: 1; }
}

/* ─── WHAT IT IS ─────────────────────────────────────── */
#what {
  padding: 80px 0;
  background: var(--bg);
  border-bottom: 1px solid var(--border);
}
.what-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: start;
}
h2 {
  font-family: var(--font-serif);
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 700;
  color: var(--navy);
  line-height: 1.2;
  letter-spacing: -0.015em;
  margin-bottom: 20px;
}
.body-text {
  font-size: 14px;
  line-height: 1.75;
  color: #444;
}
.output-items { margin-top: 32px; display: flex; flex-direction: column; gap: 1px; }
.output-item {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 16px;
  padding: 14px 0;
  border-top: 1px solid var(--border);
}
.output-item:last-child { border-bottom: 1px solid var(--border); }
.output-key { font-size: 11px; letter-spacing: 0.1em; color: var(--navy); font-weight: 500; }
.output-desc { font-size: 12px; color: var(--muted); }

/* 2×2 matrix */
.matrix-wrap { margin-top: 40px; }
.matrix-label { font-size: 11px; color: var(--muted); margin-bottom: 8px; letter-spacing: 0.08em; }
.matrix {
  display: grid;
  grid-template-columns: 80px 1fr 1fr;
  grid-template-rows: 28px 1fr 1fr;
  gap: 2px;
  font-size: 11px;
}
.matrix-cell {
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.matrix-cell.header {
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0;
}
.matrix-cell.row-header {
  background: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  color: var(--muted);
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  writing-mode: horizontal-tb;
}
.matrix-cell.top-left { background: transparent; border: none; }
.cell-label { font-weight: 500; color: var(--navy); font-size: 11px; }
.cell-desc { color: var(--muted); font-size: 10px; line-height: 1.4; }
.cell-action { color: var(--ink); font-size: 10px; margin-top: 2px; }
.matrix-cell.systemic { border-color: var(--signal); }
.matrix-cell.localized { border-color: var(--transition-c); }
.matrix-cell.emerging { border-color: #888; }
.matrix-axis-x {
  grid-column: 2 / 4;
  grid-row: 1;
  display: flex;
  gap: 2px;
}
.axis-label {
  flex: 1;
  text-align: center;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted);
  padding: 6px 0;
}

/* ─── VALIDATION ─────────────────────────────────────── */
#validation {
  padding: 80px 0;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.section-header { margin-bottom: 48px; }
.section-header p { color: var(--muted); font-size: 13px; margin-top: 8px; max-width: 540px; }
.validation-items { display: flex; flex-direction: column; gap: 0; }
.val-item {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 0;
  border-top: 1px solid var(--border);
  opacity: 0;
  transition: opacity 0.5s ease;
}
.val-item.visible { opacity: 1; }
.val-item:last-child { border-bottom: 1px solid var(--border); }
.val-meta {
  padding: 32px 32px 32px 0;
  border-right: 1px solid var(--border);
}
.val-domain {
  font-family: var(--font-serif);
  font-size: 20px;
  color: var(--navy);
  margin-bottom: 6px;
}
.val-dataset { font-size: 11px; color: var(--muted); line-height: 1.5; }
.val-trl {
  margin-top: 16px;
  display: inline-block;
  padding: 3px 10px;
  border: 1px solid var(--border);
  font-size: 10px;
  letter-spacing: 0.1em;
  color: var(--muted);
}
.val-content { padding: 32px 0 32px 32px; }
.val-finding {
  font-size: 14px;
  color: var(--ink);
  line-height: 1.65;
  margin-bottom: 20px;
}
.val-stats { display: flex; gap: 32px; flex-wrap: wrap; margin-bottom: 20px; }
.stat { }
.stat-num {
  font-family: var(--font-mono);
  font-size: 22px;
  font-weight: 500;
  color: var(--navy);
  display: block;
}
.stat-label { font-size: 10px; letter-spacing: 0.08em; color: var(--muted); text-transform: uppercase; }
.val-note {
  font-size: 11px;
  color: var(--muted);
  padding: 10px 14px;
  border-left: 2px solid var(--border);
  line-height: 1.6;
}

/* Inline SVG charts */
.chart-container {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 2px;
  padding: 16px;
  margin-top: 16px;
}
.chart-container svg { display: block; width: 100%; }

/* ─── NOT ────────────────────────────────────────────── */
#not {
  padding: 80px 0;
  background: var(--bg);
  border-bottom: 1px solid var(--border);
}
.not-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
}
.not-item {
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 28px;
}
.not-item.good { border-top: 2px solid var(--stable); }
.not-item.bad  { border-top: 2px solid var(--border); opacity: 0.7; }
.not-tag {
  font-size: 9px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin-bottom: 12px;
  font-weight: 500;
}
.not-tag.is  { color: var(--stable); }
.not-tag.not { color: var(--muted); }
.not-text { font-size: 13px; line-height: 1.65; color: var(--ink); }
.not-text em { font-style: italic; color: var(--muted); }

/* ─── HOW IT'S USED ──────────────────────────────────── */
#usage {
  padding: 80px 0;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.usage-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  margin-top: 40px;
}
.usage-item {
  background: var(--bg);
  border: 1px solid var(--border);
  padding: 28px;
  display: flex;
  flex-direction: column;
}
.usage-icon {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--navy);
  font-weight: 500;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border);
}
.usage-title { font-family: var(--font-serif); font-size: 16px; color: var(--navy); margin-bottom: 10px; }
.usage-text { font-size: 12px; color: var(--muted); line-height: 1.65; padding-bottom: 12px; }
.usage-action {
  margin-top: auto;
  font-size: 11px;
  color: var(--ink);
  padding: 8px 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 2px;
}
.usage-action::before { content: '→ '; color: var(--navy); }

/* ─── PLATFORM ───────────────────────────────────────── */
#platform {
  padding: 80px 0;
  background: var(--bg);
  border-bottom: 1px solid var(--border);
}
.platform-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}
.adapter-list { margin-top: 32px; display: flex; flex-direction: column; gap: 0; }
.adapter-item {
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-top: 1px solid var(--border);
  gap: 16px;
  font-size: 12px;
}
.adapter-item:last-child { border-bottom: 1px solid var(--border); }
.adapter-domain { color: var(--navy); font-weight: 500; width: 100px; flex-shrink: 0; }
.adapter-status {
  padding: 2px 8px;
  border-radius: 2px;
  font-size: 10px;
  letter-spacing: 0.08em;
  flex-shrink: 0;
}
.adapter-status.validated { background: rgba(42,107,74,0.1); color: var(--stable); border: 1px solid rgba(42,107,74,0.2); }
.adapter-status.poc { background: rgba(122,79,0,0.1); color: var(--transition-c); border: 1px solid rgba(122,79,0,0.2); }
.adapter-note { color: var(--muted); font-size: 11px; }

/* ─── CTA ────────────────────────────────────────────── */
#cta {
  padding: 80px 0;
  background: var(--navy);
  position: relative;
  overflow: hidden;
}
#cta::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 40px 40px;
}
.cta-inner { position: relative; text-align: center; max-width: 560px; margin: 0 auto; }
.cta-inner h2 { color: white; margin-bottom: 16px; }
.cta-inner p { color: rgba(255,255,255,0.6); font-size: 13px; margin-bottom: 36px; line-height: 1.7; }
.cta-form { display: flex; gap: 8px; max-width: 440px; margin: 0 auto; }
.cta-input {
  flex: 1;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.2);
  color: white;
  padding: 10px 16px;
  font-family: var(--font-mono);
  font-size: 12px;
  border-radius: 2px;
  outline: none;
}
.cta-input::placeholder { color: rgba(255,255,255,0.35); }
.cta-input:focus { border-color: rgba(255,255,255,0.5); }
.btn-white {
  background: white;
  color: var(--navy);
  padding: 10px 20px;
  border: 1px solid white;
  border-radius: 2px;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.06em;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
}
.btn-white:hover { opacity: 0.88; }
.cta-links {
  margin-top: 24px;
  display: flex;
  gap: 24px;
  justify-content: center;
  flex-wrap: wrap;
}
.cta-links a { color: rgba(255,255,255,0.5); font-size: 11px; text-decoration: none; letter-spacing: 0.08em; transition: color 0.2s; }
.cta-links a:hover { color: white; }
#cta-msg {
  margin-top: 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  min-height: 18px;
  transition: color 0.2s;
}
#cta-msg.success { color: var(--stable); }
#cta-msg.error { color: var(--signal); }

/* ─── FOOTER ─────────────────────────────────────────── */
footer {
  background: #0f1a2b;
  color: rgba(255,255,255,0.35);
  padding: 28px 0;
  font-size: 11px;
  letter-spacing: 0.05em;
}
.footer-inner {
  max-width: 960px; margin: 0 auto; padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
footer a { color: rgba(255,255,255,0.35); text-decoration: none; }
footer a:hover { color: rgba(255,255,255,0.6); }

/* ─── RESPONSIVE ─────────────────────────────────────── */
@media (max-width: 720px) {
  .hero-grid, .what-grid, .not-grid, .platform-inner { grid-template-columns: 1fr; gap: 40px; }
  .usage-grid { grid-template-columns: 1fr; }
  .val-item { grid-template-columns: 1fr; }
  .val-meta { border-right: none; border-bottom: 1px solid var(--border); padding: 24px 0; }
  .val-content { padding: 24px 0; }
  .cta-form { flex-direction: column; }
  .nav-links { display: none; }
  .matrix { font-size: 10px; }
}
</style>
